import type { PlayerSliceDeps } from "../deps";
import type {
  PlayerStore,
  PlaybackState,
  PlaybackActions,
  NamedSlice,
} from "../types";
import { selectCurrentItem } from "../selectors";
import {
  resolveQueueItemForPlayback,
  updateQueueItemMetadata,
} from "../utils/queuePlaybackResolution";
import type { QueueItem } from "../../utils/queue.types";
import { isOfflineAudioUrl } from "../../audio/resolveSource";
import { isOfflineAccessRefusal } from "@utils/offlineAccess/state";
import { logger } from "@utils/logger";

const clampSeekPositionMs = (positionMs: number, durationSeconds?: number) => {
  const safePositionMs = Math.max(0, positionMs);
  if (!durationSeconds || durationSeconds <= 0) return safePositionMs;
  return Math.min(safePositionMs, durationSeconds * 1000);
};

const isCurrentQueueItem = (item: QueueItem, state: PlayerStore) => {
  const latestItem = selectCurrentItem(state);
  return (
    latestItem?.metadata.id === item.metadata.id && latestItem.qid === item.qid
  );
};

type PlaybackSlice = NamedSlice<
  PlaybackState & { actions: PlaybackActions },
  PlayerStore
>;

export const createPlaybackSlice = (deps: PlayerSliceDeps): PlaybackSlice => {
  const {
    audio: audioPlayer,
    prepareTrack,
    ensureSongPlayable,
    addAudioBreadcrumb,
    observeOfflineAccess,
    showOfflineAccessPrompt,
    toast,
  } = deps;
  const { fetchFreshSongById } = deps.catalog;
  const { clearPlaybackResumeCheckpoint } = deps.resume;
  const {
    ensureProgressForPlaybackTarget,
    progressState,
    resetProgressState,
    seedProgressForPlaybackTarget,
  } = deps.progress;
  const preparePlaybackTarget = (item: QueueItem, startTimeMs?: number) => {
    const activeTrackId = audioPlayer.activeTrackId;
    const activeQueueItemId = audioPlayer.activeQueueItemId;
    if (
      activeTrackId === item.metadata.id &&
      (activeQueueItemId == null || activeQueueItemId === item.qid)
    ) {
      return false;
    }

    // The queue changes before the async playability gate finishes. Stop the
    // previous native track and seed progress for the newly displayed song so
    // its MiniPlayer can never inherit the previous track's timeline.
    if (activeTrackId) {
      const nativePlaybackState = audioPlayer.getState().playbackState;
      if (nativePlaybackState !== "paused" && nativePlaybackState !== "idle") {
        audioPlayer.pause();
      }
    }
    seedProgressForPlaybackTarget(item, startTimeMs);
    return true;
  };

  const resetFailedPlaybackTarget = (item: QueueItem, state: PlayerStore) => {
    if (!isCurrentQueueItem(item, state)) return;
    seedProgressForPlaybackTarget(item);
  };

  const shouldRequestCdnKeyRefresh = (urlOnline: string | null | undefined) =>
    Boolean(
      (urlOnline?.includes("/audio/") || urlOnline?.includes("/songs/")) &&
      !urlOnline.startsWith("file:"),
    );

  const isTemporaryOnDemandHls = (url: string) =>
    url.includes("/master.tmp.m3u8");

  /**
   * Gates a downloaded track on offline access. Returns true when this play
   * must stop: the queue moved on, or the device is offline and a known
   * user's lease does not cover it (that refusal shows the sheet).
   */
  const rejectInvalidOfflinePlayback = async (
    item: QueueItem,
    action: "play" | "playWithStartTime",
    set: Parameters<PlaybackSlice>[0],
    get: Parameters<PlaybackSlice>[1],
  ) => {
    if (!isOfflineAudioUrl(item.metadata.urlOnline)) return false;

    let access = observeOfflineAccess();
    if (access.reason === "auth_pending") {
      // Firebase is still restoring with no saved sign-in to read a lease
      // for. That is no reason to refuse: wait until it reports the user.
      addAudioBreadcrumb("rnap.offline_access.waiting_for_user", {
        songId: item.metadata.id,
        queueItemId: item.qid,
        action,
      });
      if (!(await deps.waitForOfflineAccessUser())) return true;
      const state = get();
      if (!isCurrentQueueItem(item, state)) return true;
      if (state.userIntent !== "playing") {
        // Paused while waiting: this play loaded nothing, so stop loading.
        if (state.status === "LOADING") get().actions.setStatus("IDLE");
        return true;
      }
      access = observeOfflineAccess();
    }

    const { reason } = access;
    if (!isOfflineAccessRefusal(reason) || deps.isOnline()) return false;

    logger.warn("player", "player.offline_playback_refused", {
      reason,
      songId: item.metadata.id,
      queueItemId: item.qid,
      action,
      outcome: "sheet",
    });
    showOfflineAccessPrompt(reason);
    set(
      (state) => {
        state.userIntent = "paused";
      },
      false,
      `playback/${action}/offlineAccessInvalid`,
    );
    resetFailedPlaybackTarget(item, get());
    get().actions.setStatus("ERROR");
    return true;
  };

  return (set, get) => ({
    status: "IDLE",
    userIntent: "paused",

    // Progress removed - now in shared values (progressState.ts)
    // progress: { position: 0, duration: 0 }, ❌ DELETED

    // Analytics tracking removed - now uses real-time progress from shared values

    // Ad state flattened (no nested objects)
    adPlaybackState: "idle",
    adTitle: null,
    adPosition: 0,
    adDuration: 0,
    trackChangeCount: 0,

    playbackLastError: null,
    pendingSeek: null,
    lastStreamPercentage: null,
    hasReachedPlaybackLimit: false,
    playbackLimitConfig: { enabled: false },

    actions: {
      play: async () => {
        const state = get();

        const currentItem = selectCurrentItem(state);
        if (!currentItem) {
          console.audio.warn("playback/play: no current item to play");
          return;
        }

        const pendingSeek = state.pendingSeek;
        const pendingStartTimeMs =
          pendingSeek?.songId === currentItem.metadata.id &&
          pendingSeek.queueItemId === currentItem.qid
            ? clampSeekPositionMs(
                pendingSeek.positionMs,
                currentItem.metadata.duration,
              )
            : undefined;
        const changedNativeTarget = preparePlaybackTarget(
          currentItem,
          pendingStartTimeMs,
        );

        // Set user intent - the engine will react to this declaratively
        set(
          (state) => {
            state.userIntent = "playing";
            state.pendingSeek = null;
            state.playbackLastError = null;
            if (changedNativeTarget) {
              state.status = "LOADING";
            }
          },
          false,
          "playback/play",
        );

        const resolvedItem = resolveQueueItemForPlayback(
          currentItem,
          set,
          "play",
          deps.catalog.substituteLocalSongAssets,
        );

        if (
          await rejectInvalidOfflinePlayback(resolvedItem, "play", set, get)
        ) {
          return;
        }

        try {
          // Every internal song's playback starts here: poll-gate first,
          // uniformly, no branching on any special status. See
          // `ensureSongPlayable`'s doc comment.
          const ensureResult = await ensureSongPlayable(resolvedItem.metadata);

          if (!isCurrentQueueItem(resolvedItem, get())) {
            addAudioBreadcrumb(
              "rnap.play.skipped_stale_after_ensure_playable",
              {
                songId: resolvedItem.metadata.id,
                queueItemId: resolvedItem.qid,
                pendingStartTimeMs,
              },
              "warning",
            );
            return;
          }

          if (!ensureResult.playable) {
            addAudioBreadcrumb(
              "rnap.play.not_playable",
              {
                songId: resolvedItem.metadata.id,
                queueItemId: resolvedItem.qid,
              },
              "warning",
            );
            toast.success("Coming soon on Lissen!", { duration: 1500 });
            set(
              (state) => {
                state.userIntent = "paused";
              },
              false,
              "playback/play/notPlayable",
            );
            resetFailedPlaybackTarget(resolvedItem, get());
            get().actions.setStatus("ERROR");
            return;
          }

          // The poll gate may have re-fetched the song or crossed the tmp/final
          // master mid-poll — when it did, `ensureResult.song` is a NEW object;
          // land it back in the live queue and play from it. Never assign onto
          // `resolvedItem` itself: when no local-asset substitution happened,
          // `resolveQueueItemForPlayback` returns the store's own (immer-frozen)
          // queue item, and mutating it throws
          // `Cannot assign to read-only property 'metadata'`.
          const playbackItem =
            ensureResult.song !== resolvedItem.metadata
              ? { ...resolvedItem, metadata: ensureResult.song }
              : resolvedItem;
          if (playbackItem !== resolvedItem) {
            updateQueueItemMetadata(
              resolvedItem,
              playbackItem,
              set,
              "playback/play/refreshAfterEnsurePlayable",
            );
          }

          const track = await prepareTrack(playbackItem.metadata);
          if (!isCurrentQueueItem(playbackItem, get())) {
            addAudioBreadcrumb(
              "rnap.play.skipped_stale_prepared_track",
              {
                songId: playbackItem.metadata.id,
                queueItemId: playbackItem.qid,
                pendingStartTimeMs,
              },
              "warning",
            );
            return;
          }
          const startTimeMs =
            pendingStartTimeMs ??
            (isTemporaryOnDemandHls(track.url) ? 0 : undefined);
          audioPlayer.play(
            { ...track, queueItemId: playbackItem.qid },
            startTimeMs != null ? { startTimeMs } : undefined,
          );
        } catch (error) {
          console.audio.error("playback/play: failed to prepare track", {
            songId: resolvedItem.metadata.id,
            error: (error as Error).message,
          });
          resetFailedPlaybackTarget(resolvedItem, get());
          get().actions.setStatus("ERROR");
        }
      },

      playWithStartTime: async (startTimeMs: number) => {
        const currentItem = selectCurrentItem(get());
        if (!currentItem) {
          console.audio.warn("playback/playWithStartTime: no current item");
          return;
        }
        const changedNativeTarget = preparePlaybackTarget(
          currentItem,
          startTimeMs,
        );

        set(
          (state) => {
            state.userIntent = "playing";
            state.playbackLastError = null;
            if (changedNativeTarget) {
              state.status = "LOADING";
            }
          },
          false,
          "playback/playWithStartTime",
        );

        const resolvedItem = resolveQueueItemForPlayback(
          currentItem,
          set,
          "playWithStartTime",
          deps.catalog.substituteLocalSongAssets,
        );

        if (
          await rejectInvalidOfflinePlayback(
            resolvedItem,
            "playWithStartTime",
            set,
            get,
          )
        ) {
          return;
        }

        try {
          // Same universal poll-gate as `play` — see its comment.
          const ensureResult = await ensureSongPlayable(resolvedItem.metadata);

          if (!isCurrentQueueItem(resolvedItem, get())) {
            addAudioBreadcrumb(
              "rnap.play_with_start_time.skipped_stale_after_ensure_playable",
              {
                songId: resolvedItem.metadata.id,
                queueItemId: resolvedItem.qid,
                startTimeMs,
              },
              "warning",
            );
            return;
          }

          if (!ensureResult.playable) {
            addAudioBreadcrumb(
              "rnap.play_with_start_time.not_playable",
              {
                songId: resolvedItem.metadata.id,
                queueItemId: resolvedItem.qid,
                startTimeMs,
              },
              "warning",
            );
            toast.success("Coming soon on Lissen!", { duration: 1500 });
            set(
              (state) => {
                state.userIntent = "paused";
              },
              false,
              "playback/playWithStartTime/notPlayable",
            );
            resetFailedPlaybackTarget(resolvedItem, get());
            get().actions.setStatus("ERROR");
            return;
          }

          // Same never-mutate rule as `play` above — `resolvedItem` may be the
          // store's frozen queue item.
          const playbackItem =
            ensureResult.song !== resolvedItem.metadata
              ? { ...resolvedItem, metadata: ensureResult.song }
              : resolvedItem;
          if (playbackItem !== resolvedItem) {
            updateQueueItemMetadata(
              resolvedItem,
              playbackItem,
              set,
              "playback/playWithStartTime/refreshAfterEnsurePlayable",
            );
          }

          const track = await prepareTrack(playbackItem.metadata);
          if (!isCurrentQueueItem(playbackItem, get())) {
            addAudioBreadcrumb(
              "rnap.play_with_start_time.skipped_stale_prepared_track",
              {
                songId: playbackItem.metadata.id,
                queueItemId: playbackItem.qid,
                startTimeMs,
              },
              "warning",
            );
            return;
          }
          audioPlayer.play(
            { ...track, queueItemId: playbackItem.qid },
            { startTimeMs },
          );
        } catch (error) {
          console.audio.error("playback/playWithStartTime: failed", {
            songId: resolvedItem.metadata.id,
            error: (error as Error).message,
          });
          // Prevent infinite retry - surrender on failure
          set(
            (state) => {
              state.userIntent = "paused";
            },
            false,
            "playback/playWithStartTime/failed",
          );
          resetFailedPlaybackTarget(resolvedItem, get());
          get().actions.setStatus("ERROR");
        }
      },

      recoverFromSourceError: async ({
        startTimeMs,
        attempt,
        errorCode,
        message,
      }) => {
        const currentItem = selectCurrentItem(get());
        if (!currentItem) {
          addAudioBreadcrumb(
            "rnap.source_recovery.skipped_no_current_item",
            { startTimeMs, attempt, errorCode, message },
            "warning",
          );
          return;
        }

        const songId = currentItem.metadata.id;
        const queueItemId = currentItem.qid;
        addAudioBreadcrumb(
          "rnap.source_recovery.start",
          { songId, queueItemId, startTimeMs, attempt, errorCode, message },
          "warning",
        );

        set(
          (state) => {
            state.userIntent = "playing";
          },
          false,
          "playback/recoverFromSourceError",
        );

        try {
          addAudioBreadcrumb(
            "rnap.source_recovery.fetch_song.start",
            { songId, queueItemId, attempt },
            "warning",
          );
          const freshSong = await fetchFreshSongById(songId);
          if (!freshSong) {
            throw new Error(`Song ${songId} not found in catalog`);
          }
          addAudioBreadcrumb(
            "rnap.source_recovery.fetch_song.success",
            {
              songId,
              queueItemId,
              attempt,
              hasUrlOnline: Boolean(freshSong.urlOnline),
              requestCdnKeyRefresh: shouldRequestCdnKeyRefresh(
                freshSong.urlOnline,
              ),
            },
            "warning",
          );

          if (!isCurrentQueueItem(currentItem, get())) {
            addAudioBreadcrumb(
              "rnap.source_recovery.skipped_stale_prepared_track",
              { songId, queueItemId, startTimeMs, attempt },
              "warning",
            );
            return;
          }

          if (shouldRequestCdnKeyRefresh(freshSong.urlOnline)) {
            addAudioBreadcrumb(
              "rnap.source_recovery.cdn_key.refresh.start",
              { songId, queueItemId, attempt },
              "warning",
            );
          }

          const track = await prepareTrack(freshSong, {
            forceAsyncSourceResolution: true,
            forceRefreshCDNKey: true,
            sourceRecoveryAttempt: attempt,
          });

          if (!isCurrentQueueItem(currentItem, get())) {
            addAudioBreadcrumb(
              "rnap.source_recovery.skipped_stale_recovered_track",
              { songId, queueItemId, startTimeMs, attempt },
              "warning",
            );
            return;
          }

          addAudioBreadcrumb(
            "rnap.source_recovery.retry_play",
            {
              songId,
              queueItemId,
              startTimeMs,
              attempt,
              sourceType: track.sourceType ?? "unknown",
              cdnKeyRefreshRequested: track.cdnKeyRefreshRequested ?? false,
            },
            "warning",
          );
          audioPlayer.play(
            { ...track, queueItemId: currentItem.qid },
            { startTimeMs },
          );
        } catch (error) {
          console.audio.error("playback/recoverFromSourceError: failed", {
            songId,
            attempt,
            error: (error as Error).message,
          });
          addAudioBreadcrumb(
            "rnap.source_recovery.retry_failed",
            {
              songId,
              queueItemId,
              startTimeMs,
              attempt,
              error: (error as Error).message,
            },
            "error",
          );
          get().actions.setStatus("ERROR");
        }
      },

      pause: async () => {
        // Set user intent - the engine will react to this declaratively
        set(
          (state) => {
            state.userIntent = "paused";
          },
          false,
          "playback/pause",
        );
        await audioPlayer.pause();
      },

      resume: async () => {
        set(
          (state) => {
            state.userIntent = "playing";
          },
          false,
          "playback/resume",
        );
        await audioPlayer.resume();
      },

      seek: async (time) => {
        const currentItem = selectCurrentItem(get());
        const requestedPositionMs = time * 1000;

        if (!currentItem) {
          addAudioBreadcrumb(
            "rnap.seek.skipped_no_current_item",
            { requestedPositionMs },
            "warning",
          );
          return;
        }

        const positionMs = clampSeekPositionMs(
          requestedPositionMs,
          currentItem.metadata.duration,
        );
        const nativeTrackId = audioPlayer.activeTrackId;

        if (nativeTrackId === currentItem.metadata.id) {
          await audioPlayer.seekTo(positionMs);
          return;
        }

        addAudioBreadcrumb(
          "rnap.seek.deferred_no_matching_native_track",
          {
            songId: currentItem.metadata.id,
            queueItemId: currentItem.qid,
            nativeTrackId,
            requestedPositionMs,
            positionMs,
            durationMs: currentItem.metadata.duration
              ? currentItem.metadata.duration * 1000
              : null,
          },
          "warning",
        );
        set(
          (state) => {
            state.pendingSeek = {
              songId: currentItem.metadata.id,
              queueItemId: currentItem.qid,
              positionMs,
            };
          },
          false,
          "playback/seek/deferred",
        );
        ensureProgressForPlaybackTarget(currentItem);
        progressState.position.value = positionMs / 1000;
        if (currentItem.metadata.duration) {
          progressState.duration.value = currentItem.metadata.duration;
        }
      },

      // syncProgress removed - progress now in shared values
      // syncProgress: (position, duration, buffered) => { ... } ❌ DELETED

      setStatus: (status) => {
        set(
          (state) => {
            state.status = status;
          },
          false,
          "playback/setStatus",
        );
      },

      setUserIntent: (intent) => {
        set(
          (state) => {
            state.userIntent = intent;
          },
          false,
          "playback/setUserIntent",
        );
      },

      setLastStreamPercentage: (songId, percentage) => {
        set(
          (state) => {
            state.lastStreamPercentage = { songId, percentage };
          },
          false,
          "playback/setLastStreamPercentage",
        );
      },

      setPlaybackLastError: (error) => {
        set(
          (state) => {
            state.playbackLastError = error;
          },
          false,
          "playback/setPlaybackLastError",
        );
      },

      setHasReachedPlaybackLimit: (reached) => {
        set(
          (state) => {
            state.hasReachedPlaybackLimit = reached;
          },
          false,
          "playback/setHasReachedPlaybackLimit",
        );
      },

      setPlaybackLimitConfig: (config) => {
        set(
          (state) => {
            state.playbackLimitConfig = config;
          },
          false,
          "playback/setPlaybackLimitConfig",
        );
      },

      setTrackChangeCount: (count) => {
        set(
          (state) => {
            state.trackChangeCount = count;
          },
          false,
          "playback/setTrackChangeCount",
        );
      },

      reset: () => {
        clearPlaybackResumeCheckpoint();
        resetProgressState();
        set(
          (state) => {
            state.status = "IDLE";
            state.userIntent = "paused";
            // Progress removed - reset handled in shared values
            // state.progress = { position: 0, duration: 0 }; ❌ DELETED
            // Analytics tracking removed - reset handled in shared values
            state.playbackLastError = null;
            state.pendingSeek = null;
            state.playlistQueue = null;
            state.foryouQueue = null;
            state.activeQueueType = "foryou";
            // Ad state flattened
            state.adPlaybackState = "idle";
            state.adTitle = null;
            state.adPosition = 0;
            state.adDuration = 0;
            state.trackChangeCount = 0;
            state.hasReachedPlaybackLimit = false;
            state.playbackLimitConfig = { enabled: false };
          },
          false,
          "playback/reset",
        );
      },
    },
  });
};
